import React, { useEffect, useState } from 'react';
import { Modal, Table, Button, Badge, Spinner, Form, Accordion, Card } from 'react-bootstrap';
import { getDataList } from '../../api/common';
import { CHILD } from '../../constants';
import moment from 'moment';
import 'moment/locale/es';
import ModalObservacionesHT from './ModalObservacionesHT';
import ModalConfirmarEmailHT from './ModalConfirmarEmailHT';
import { PdfOt } from '../Ot/PdfOt';
import { Alertassw } from '../Alertas/Alertassw';

/**
 * Modal principal para listar y gestionar Hojas de Trabajo (Child OTs)
 * Incluye cache local, columna de estado de firma y acciones completas
 */
const ModalHojasTrabajo = ({ show, onHide, numeroOrden, cache, setCache, onRefresh }) => {
  const [hojasTrabajo, setHojasTrabajo] = useState(cache || []);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estados para modales secundarios
  const [showObservaciones, setShowObservaciones] = useState(false);
  const [showConfirmarEmail, setShowConfirmarEmail] = useState(false);
  const [selectedHT, setSelectedHT] = useState(null);
  const [emailAction, setEmailAction] = useState(''); // 'resend' o 'send'

  /**
   * Cargar Hojas de Trabajo solo al abrir el modal o cuando se fuerza refresh
   * Cache se resetea al cerrar el modal (ver handleHide)
   */
  useEffect(() => {
    if (show && !cache) {
      cargarHojasTrabajo();
    } else if (show && cache) {
      setHojasTrabajo(cache);
    }
  }, [show, cache]);

  const cargarHojasTrabajo = async () => {
    try {
      setLoading(true);
      console.log('📋 [ModalHT] Cargando Hojas de Trabajo para OT:', numeroOrden);
      
      const response = await getDataList('Ot', {
        conditions: { 
          numeroOrden: numeroOrden, 
          otType: CHILD, 
          estado: 'Cerrada' 
        }
      });
      
      if (response.status === 200 && response.data?.data) {
        const hts = response.data.data;
        console.log(`✅ [ModalHT] Cargadas ${hts.length} Hojas de Trabajo`);
        setHojasTrabajo(hts);
        setCache(hts); // Guardar en cache del componente padre
      } else {
        console.error('❌ [ModalHT] Error al cargar HT:', response);
        Alertassw('error', 'Error al cargar Hojas de Trabajo', 'Intente nuevamente');
      }
    } catch (error) {
      console.error('❌ [ModalHT] Error al cargar HT:', error);
      Alertassw('error', 'Error al cargar Hojas de Trabajo', error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calcular estado de firma de una HT
   * - Firmada: recibido.firma existe
   * - Enviada para firmar: firmaClienteEnviada === true sin recibido.firma
   * - Sin firmar: resto de casos
   */
  const calcularEstadoFirma = (ht) => {
    if (ht.recibido?.firma) {
      return { texto: 'Firmada', variant: 'success', icon: '✅' };
    }
    if (ht.firmaClienteEnviada === true && !ht.recibido?.firma) {
      return { texto: 'Enviada para firmar', variant: 'warning', icon: '📧' };
    }
    return { texto: 'Sin firmar', variant: 'secondary', icon: '⏳' };
  };

  /**
   * Filtrar HT por búsqueda
   */
  const htsFiltradas = hojasTrabajo.filter((ht) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      `${ht.numeroOrden}-${ht.visitas}`.toLowerCase().includes(query) ||
      `${ht.responsable?.nombre} ${ht.responsable?.apellido}`.toLowerCase().includes(query)
    );
  });

  /**
   * Acciones de la tabla
   */
  const handleVerObservaciones = (ht) => {
    setSelectedHT(ht);
    setShowObservaciones(true);
  };

  const handleDescargarPDF = async (ht) => {
    try {
      Alertassw('toast', 'Descargando PDF, espera unos segundos...');
      await PdfOt(ht, ht.entregado?.firma, ht.recibido?.firma, false);
    } catch (error) {
      Alertassw('error', 'Error al descargar PDF', error.message);
    }
  };

  const handleEnviarEmail = (ht) => {
    setSelectedHT(ht);
    setEmailAction('send');
    setShowConfirmarEmail(true);
  };

  const handleReenviarFirma = (ht) => {
    setSelectedHT(ht);
    setEmailAction('resend');
    setShowConfirmarEmail(true);
  };

  const handleRefresh = () => {
    setCache(null); // Limpiar cache
    cargarHojasTrabajo();
  };

  const handleHide = () => {
    setCache(null); // Resetear cache al cerrar modal
    setSearchQuery('');
    onHide();
  };

  return (
    <>
      <Modal show={show} onHide={handleHide} size="xl" centered scrollable>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-file-earmark-text me-2"></i>
            Hojas de Trabajo - OT {numeroOrden}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Barra de búsqueda y refresh */}
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              type="text"
              placeholder="Buscar por consecutivo o responsable..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button 
              variant="outline-primary" 
              onClick={handleRefresh}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise"></i>
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando Hojas de Trabajo...</p>
            </div>
          ) : htsFiltradas.length === 0 ? (
            <div className="alert alert-info text-center">
              <i className="bi bi-info-circle me-2"></i>
              {searchQuery ? 'No se encontraron Hojas de Trabajo con ese criterio' : 'No hay Hojas de Trabajo cerradas para esta OT'}
            </div>
          ) : (
            <Accordion>
              {htsFiltradas.map((ht, index) => {
                const estadoFirma = calcularEstadoFirma(ht);
                const totalEquipos = ht.equipos?.length || 0;
                
                return (
                  <Accordion.Item eventKey={index.toString()} key={ht._id}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div className="d-flex align-items-center gap-3 flex-grow-1">
                          <strong className="text-primary">{ht.numeroOrden}-{ht.visitas}</strong>
                          <span className="text-muted">
                            {ht.responsable?.nombre} {ht.responsable?.apellido}
                          </span>
                          <span className="text-muted small">
                            {ht.fechaRealEntrega ? moment(ht.fechaRealEntrega).format('L') : moment(ht.fechaCierre).format('L')}
                          </span>
                          <Badge bg="info">{totalEquipos} equipos</Badge>
                          <Badge bg={estadoFirma.variant}>
                            {estadoFirma.icon} {estadoFirma.texto}
                          </Badge>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {/* Acciones */}
                      <div className="d-flex gap-2 mb-3 justify-content-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Ver observaciones"
                          onClick={() => handleVerObservaciones(ht)}
                        >
                          <i className="bi bi-eye-fill me-1"></i>
                          Observaciones
                        </Button>

                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Descargar PDF"
                          onClick={() => handleDescargarPDF(ht)}
                        >
                          <i className="bi bi-file-earmark-pdf me-1"></i>
                          Descargar PDF
                        </Button>

                        <Button
                          variant="outline-success"
                          size="sm"
                          title={ht.recibido?.firma ? "Enviar PDF por correo" : "Solo disponible para HT firmadas"}
                          disabled={!ht.recibido?.firma}
                          onClick={() => handleEnviarEmail(ht)}
                        >
                          <i className="bi bi-envelope me-1"></i>
                          Enviar Email
                        </Button>

                        <Button
                          variant="outline-warning"
                          size="sm"
                          title={ht.firmaClienteEnviada && !ht.recibido?.firma ? "Reenviar solicitud de firma" : "No aplica"}
                          disabled={!ht.firmaClienteEnviada || ht.recibido?.firma}
                          onClick={() => handleReenviarFirma(ht)}
                        >
                          <i className="bi bi-arrow-repeat me-1"></i>
                          Reenviar Firma
                        </Button>
                      </div>

                      {/* Tabla de equipos */}
                      {totalEquipos > 0 ? (
                        <Table striped hover size="sm" responsive>
                          <thead className="table-secondary">
                            <tr>
                              <th>Ítem</th>
                              <th>Marca</th>
                              <th>Modelo</th>
                              <th>Serie</th>
                              <th>Inventario</th>
                              <th>Ubicación</th>
                              <th>Resolución</th>
                              <th>Puntos</th>
                              <th>Consecutivo</th>
                              <th>Estado</th>
                              <th>Obs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ht.equipos.map((equipo, eqIndex) => {
                              // Usar snapshot si existe (optimización spec 003), sino equipo poblado
                              const itemNombre = equipo.snapshot?.item || equipo.equipo?.item?.nombre || 'N/A';
                              const marca = equipo.snapshot?.marca || equipo.equipo?.marca || '';
                              const modelo = equipo.snapshot?.modelo || equipo.equipo?.modelo || '';
                              const serie = equipo.snapshot?.serial || equipo.equipo?.serie || '';
                              const inventario = equipo.snapshot?.inventario || equipo.equipo?.inventario || '';
                              const ubicacion = equipo.snapshot?.ubicacion || equipo.equipo?.ubicacion || '';
                              const resolucion = equipo.snapshot?.resolucion || equipo.equipo?.resolucion || '';
                              const puntos = equipo.snapshot?.puntos || equipo.equipo?.puntos || '';
                              
                              return (
                                <tr key={equipo._id || eqIndex}>
                                  <td>{itemNombre}</td>
                                  <td>{marca}</td>
                                  <td>{modelo}</td>
                                  <td>{serie}</td>
                                  <td>{inventario}</td>
                                  <td>{ubicacion}</td>
                                  <td>{resolucion}</td>
                                  <td>{puntos}</td>
                                  <td><Badge bg="secondary">{equipo.consecutivo}</Badge></td>
                                  <td>
                                    <Badge bg={
                                      equipo.estado === 'Finalizado' ? 'success' :
                                      equipo.estado === 'Procesado' ? 'info' :
                                      equipo.estado === 'Aplazado' ? 'warning' :
                                      equipo.estado === 'Cancelado' ? 'danger' :
                                      'secondary'
                                    }>
                                      {equipo.estado}
                                    </Badge>
                                  </td>
                                  <td>
                                    {equipo.observacion && (
                                      <i 
                                        className="bi bi-eye-fill text-primary cursor-pointer" 
                                        title={equipo.observacion}
                                        style={{ cursor: 'pointer' }}
                                      ></i>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="alert alert-warning">No hay equipos registrados en esta HT</div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <div className="text-muted small me-auto">
            {htsFiltradas.length} de {hojasTrabajo.length} Hojas de Trabajo
          </div>
          <Button variant="secondary" onClick={handleHide}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal secundario: Observaciones */}
      {selectedHT && (
        <ModalObservacionesHT
          show={showObservaciones}
          onHide={() => {
            setShowObservaciones(false);
            setSelectedHT(null);
          }}
          ht={selectedHT}
        />
      )}

      {/* Modal secundario: Confirmar Email */}
      {selectedHT && (
        <ModalConfirmarEmailHT
          show={showConfirmarEmail}
          onHide={() => {
            setShowConfirmarEmail(false);
            setSelectedHT(null);
            setEmailAction('');
          }}
          ht={selectedHT}
          action={emailAction}
          onSuccess={() => {
            handleRefresh(); // Recargar lista después de acción exitosa
            if (onRefresh) onRefresh(); // Notificar al componente padre
          }}
        />
      )}
    </>
  );
};

export default ModalHojasTrabajo;
